import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, Copy, Check } from 'lucide-react';
import CoinLogo from '../components/CoinLogo';

export default function BrandPage() {
  const [copied, setCopied] = useState(false);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-surface-base">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-surface-base/80 border-b border-border-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <CoinLogo size={40} animate={false} pauseOnHover={false} />
              <span className="font-clash text-xl font-semibold gold-text">CoinHustle Exchange</span>
            </Link>
            <Link to="/" className="text-sm text-zinc-400 hover:text-white flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Hero */}
        <div className="text-center mb-20">
          <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold mb-4">
            Brand Assets
          </p>
          <h1 className="font-clash text-4xl sm:text-5xl lg:text-6xl font-semibold text-white mb-6">
            CoinHustle Logo
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
            Our premium 3D animated coin logo represents trust, value, and the excitement of asset trading.
          </p>
        </div>

        {/* Main Logo Showcase */}
        <section className="mb-24">
          <div className="relative p-16 bg-gradient-to-b from-surface-card to-surface-base border border-border-subtle overflow-hidden">
            {/* Background pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{ 
                backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(212,175,55,0.1) 10px, rgba(212,175,55,0.1) 20px)'
              }} />
            </div>
            
            {/* Spotlight */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-96 h-96 bg-gold/10 rounded-full blur-3xl" />
            
            <div className="relative flex flex-col items-center">
              <CoinLogo size={250} animate={true} pauseOnHover={true} />
              <p className="mt-8 text-sm text-zinc-500">Hover to pause animation</p>
            </div>
          </div>
        </section>

        {/* Logo Variations */}
        <section className="mb-24">
          <h2 className="font-clash text-2xl font-medium text-white mb-8">Logo Variations</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Animated */}
            <div className="p-8 bg-surface-card border border-border-subtle">
              <div className="flex justify-center mb-6">
                <CoinLogo size={100} animate={true} pauseOnHover={false} />
              </div>
              <h3 className="font-clash text-lg font-medium text-white text-center mb-2">Animated</h3>
              <p className="text-sm text-zinc-400 text-center">Full rotation animation for hero sections</p>
            </div>

            {/* Static */}
            <div className="p-8 bg-surface-card border border-border-subtle">
              <div className="flex justify-center mb-6">
                <CoinLogo size={100} animate={false} pauseOnHover={false} />
              </div>
              <h3 className="font-clash text-lg font-medium text-white text-center mb-2">Static</h3>
              <p className="text-sm text-zinc-400 text-center">Angled view for headers and navigation</p>
            </div>

            {/* With Text */}
            <div className="p-8 bg-surface-card border border-border-subtle">
              <div className="flex justify-center mb-6">
                <CoinLogo size={80} animate={false} pauseOnHover={false} showText={true} />
              </div>
              <h3 className="font-clash text-lg font-medium text-white text-center mb-2">With Text</h3>
              <p className="text-sm text-zinc-400 text-center">Full brand lockup with wordmark</p>
            </div>
          </div>
        </section>

        {/* Size Examples */}
        <section className="mb-24">
          <h2 className="font-clash text-2xl font-medium text-white mb-8">Size Scaling</h2>
          
          <div className="p-8 bg-surface-card border border-border-subtle">
            <div className="flex items-end justify-center gap-8 flex-wrap">
              {[200, 120, 80, 60, 40, 32].map(size => (
                <div key={size} className="flex flex-col items-center">
                  <CoinLogo size={size} animate={false} pauseOnHover={false} />
                  <span className="mt-4 text-xs text-zinc-500">{size}px</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Usage on Backgrounds */}
        <section className="mb-24">
          <h2 className="font-clash text-2xl font-medium text-white mb-8">Background Usage</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Dark Background */}
            <div className="p-12 bg-black flex flex-col items-center border border-border-subtle">
              <CoinLogo size={120} animate={true} />
              <p className="mt-6 text-sm text-zinc-400">Dark Background (Recommended)</p>
            </div>

            {/* Charcoal Background */}
            <div className="p-12 bg-zinc-900 flex flex-col items-center border border-border-subtle">
              <CoinLogo size={120} animate={true} />
              <p className="mt-6 text-sm text-zinc-400">Charcoal Background</p>
            </div>
          </div>
        </section>

        {/* Color Palette */}
        <section className="mb-24">
          <h2 className="font-clash text-2xl font-medium text-white mb-8">Brand Colors</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {[
              { name: 'Gold Primary', hex: '#F0BD13', desc: 'Main brand color' },
              { name: 'Gold Light', hex: '#FFF0A0', desc: 'Highlights' },
              { name: 'Gold Dark', hex: '#C49510', desc: 'Shadows' },
              { name: 'Gold Deep', hex: '#8B6914', desc: 'Accents' },
              { name: 'Background', hex: '#0A0A0B', desc: 'Primary bg' },
              { name: 'Surface', hex: '#141415', desc: 'Cards' },
            ].map(color => (
              <button
                key={color.hex}
                onClick={() => handleCopy(color.hex)}
                className="p-4 bg-surface-card border border-border-subtle hover:border-gold/50 transition-colors group"
              >
                <div 
                  className="w-full aspect-square mb-3 border border-border-subtle"
                  style={{ backgroundColor: color.hex }}
                />
                <p className="text-sm font-medium text-white">{color.name}</p>
                <p className="text-xs text-zinc-500 flex items-center justify-between">
                  {color.hex}
                  {copied ? (
                    <Check className="w-3 h-3 text-emerald-500" />
                  ) : (
                    <Copy className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                  )}
                </p>
              </button>
            ))}
          </div>
        </section>

        {/* Typography */}
        <section className="mb-24">
          <h2 className="font-clash text-2xl font-medium text-white mb-8">Typography</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-8 bg-surface-card border border-border-subtle">
              <p className="text-xs text-gold uppercase tracking-wider mb-4">Primary Font</p>
              <p className="font-clash text-5xl font-semibold text-white mb-2">Clash Display</p>
              <p className="font-clash text-lg text-zinc-400">
                Used for headings, logo text, and emphasis
              </p>
              <div className="mt-6 pt-6 border-t border-border-subtle">
                <p className="font-clash text-2xl text-white">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
                <p className="font-clash text-2xl text-white">abcdefghijklmnopqrstuvwxyz</p>
                <p className="font-clash text-2xl text-white">0123456789</p>
              </div>
            </div>

            <div className="p-8 bg-surface-card border border-border-subtle">
              <p className="text-xs text-gold uppercase tracking-wider mb-4">Body Font</p>
              <p className="font-manrope text-5xl font-semibold text-white mb-2">Manrope</p>
              <p className="font-manrope text-lg text-zinc-400">
                Used for body text, descriptions, and UI elements
              </p>
              <div className="mt-6 pt-6 border-t border-border-subtle">
                <p className="font-manrope text-2xl text-white">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
                <p className="font-manrope text-2xl text-white">abcdefghijklmnopqrstuvwxyz</p>
                <p className="font-manrope text-2xl text-white">0123456789</p>
              </div>
            </div>
          </div>
        </section>

        {/* Brand Statement */}
        <section className="text-center py-16 border-t border-border-subtle">
          <CoinLogo size={80} animate={true} pauseOnHover={true} showText={true} className="justify-center mb-8" />
          <p className="text-2xl text-zinc-400 font-clash italic">
            "Where Collectors and Capital Meet"
          </p>
        </section>
      </main>
    </div>
  );
}
